import { NormalizedRect, Point, Rect, Size, rectFromPoints } from './geometry'

/**
 * Represents a 2D linear transformation in the form of a 3x3 matrix, where the last row is always [0, 0, 1].
 *
 * ```
 *  | a  b  tx |
 *  | c  d  ty |
 *  | 0  0   1 |
 * ```
 */
export type LinearTransform = {
  a: number
  b: number
  c: number
  d: number
  tx: number
  ty: number
}

function isRect(shape: Point | Rect): shape is Rect {
  return 'width' in shape && 'height' in shape
}

const IDENTITY = Object.freeze({
  a: 1,
  b: 0,
  c: 0,
  d: 1,
  tx: 0,
  ty: 0
}) as Readonly<LinearTransform>

function apply(shape: Rect, transform: LinearTransform): Rect
function apply(shape: Point, transform: LinearTransform): Point
function apply(shape: Point | Rect, transform: LinearTransform): Point | Rect {
  // optimization where if we have the identity transform we can return the original
  // thing unchanged
  if (transform === IDENTITY) {
    return shape
  }

  if (isRect(shape)) {
    const p1 = apply({ x: shape.x, y: shape.y }, transform)
    const p2 = apply({ x: shape.x + shape.width, y: shape.y + shape.height }, transform)
    return rectFromPoints(p1, p2)
  } else {
    return {
      x: transform.a * shape.x + transform.b * shape.y + transform.tx,
      y: transform.c * shape.x + transform.d * shape.y + transform.ty
    }
  }
}

export const LinearTransform = {
  apply,

  toCSS(transform: LinearTransform): string {
    // CSS matrix(a, b, c, d, e, f) maps (x, y) to (a*x + c*y + e, b*x + d*y + f)
    // Our internal apply uses x' = a*x + b*y + tx, y' = c*x + d*y + ty
    // So mapping is: a_css = a, b_css = c, c_css = b, d_css = d, e_css = tx, f_css = ty
    return `matrix(${transform.a}, ${transform.c}, ${transform.b}, ${transform.d}, ${transform.tx}, ${transform.ty})`
  },

  toSVG(transform: LinearTransform): string {
    // SVG matrix(a b c d e f) uses the same mapping as CSS
    return `matrix(${transform.a} ${transform.c} ${transform.b} ${transform.d} ${transform.tx} ${transform.ty})`
  },

  IDENTITY: IDENTITY,

  equals(a: LinearTransform, b: LinearTransform): boolean {
    return (
      a.a === b.a && a.b === b.b && a.c === b.c && a.d === b.d && a.tx === b.tx && a.ty === b.ty
    )
  },

  scale(factor: number): LinearTransform {
    return { a: factor, b: 0, c: 0, d: factor, tx: 0, ty: 0 }
  },

  invert(transform: LinearTransform): LinearTransform {
    const denom = transform.a * transform.d - transform.b * transform.c

    if (denom === 0) {
      throw new Error('Matrix not invertible')
    }

    return {
      a: transform.d / denom,
      b: transform.b / -denom,
      c: transform.c / -denom,
      d: transform.a / denom,
      tx: (transform.d * transform.tx - transform.b * transform.ty) / -denom,
      ty: (transform.c * transform.tx - transform.a * transform.ty) / denom
    }
  },

  /**
   * Creates a transformation that flips coordinates vertically around the specified y-axis.
   *
   * @param axisY The y-coordinate of the axis to flip around (typically the vertical center of the viewport).
   * @returns A LinearTransform that performs the vertical flip.
   *
   * Common Use Case:
   *   - Converting between coordinate systems where the y-axis origin is at the top (e.g., web browsers)
   *     and those where the origin is at the bottom (e.g., some PDFs or graphics libraries).
   */
  flipVertical(axisY: number): LinearTransform {
    return { a: 1, b: 0, c: 0, d: -1, tx: 0, ty: axisY * 2 }
  },

  /**
   * Creates a transformation that normalizes coordinates from the actual page dimensions to the [0, 1] range.
   *
   * Normalized coordinates
   *
   * @param size The dimensions of the page ({ width: number, height: number })
   * @returns A LinearTransform that normalizes coordinates.
   */
  normalize(size: Size): LinearTransform {
    return {
      a: 1 / size.width,
      b: 0,
      c: 0,
      d: 1 / size.height,
      tx: 0,
      ty: 0
    }
  },

  /**
   * Creates a transformation that denormalizes coordinates from the [0, 1] range to the actual page dimensions.
   *
   * @param size The dimensions of the page ({ width: number, height: number })
   * @returns A LinearTransform that denormalizes coordinates.
   */
  denormalize(size: Size): LinearTransform {
    return {
      a: size.width,
      b: 0,
      c: 0,
      d: size.height,
      tx: 0,
      ty: 0
    }
  },

  /**
   * Create a page-anchored rotation transform for multiples of 90 degrees.
   * The rotation is applied within the bounds of a page of the given size,
   * with the origin at the top-left. The returned matrix maps the page into
   * a non-negative coordinate space after rotation by translating appropriately.
   *
   * deg: rotation in degrees (clockwise). Only 0, 90, 180, 270 are supported.
   * size: the unrotated page size { width: W, height: H }.
   *
   * The origin is at the top-left with coordinates increasing down and to the right.
   */
  pageRotate(deg: number, size: Size): LinearTransform {
    const W = size.width
    const H = size.height

    switch (deg) {
      case 0:
        return { a: 1, b: 0, c: 0, d: 1, tx: 0, ty: 0 }
      // 90° clockwise: (x', y') = (-y + H, x) with top-left origin
      // To keep the rotated page in the positive quadrant (and top-left aligned),
      // translate RIGHT by the original height H.
      case 90:
        return { a: 0, b: -1, c: 1, d: 0, tx: H, ty: 0 }
      // 180°: (x', y') = (-x + W, -y + H)
      case 180:
        return { a: -1, b: 0, c: 0, d: -1, tx: W, ty: H }
      // 270° clockwise: (x', y') = (y, -x + W) with top-left origin
      // To keep the rotated page in the positive quadrant (and top-left aligned),
      // translate DOWN by the original width W.
      case 270:
        return { a: 0, b: 1, c: -1, d: 0, tx: 0, ty: W }
      default:
        throw new Error(`pageRotate only supports multiples of 90 degrees (got ${deg})`)
    }
  }
}

export function normalizeRect(rect: Rect, bounds: Rect): NormalizedRect {
  return apply(rect, {
    ...LinearTransform.normalize(bounds),
    tx: -bounds.x / bounds.width,
    ty: -bounds.y / bounds.height
  })
}

export function denormalizeRect(rect: NormalizedRect, bounds: Rect): Rect {
  return apply(rect, {
    ...LinearTransform.denormalize(bounds),
    tx: bounds.x,
    ty: bounds.y
  })
}
